import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Search, CheckSquare, Square, Zap } from 'lucide-react';

/**
 * Multi-select serial picker.
 *
 * Props:
 *   isOpen, onClose
 *   product: { id, name, brand, model, serials: [{ sn, status }] }
 *   excludedSerials: string[]   — serials already used elsewhere (this invoice)
 *   defaultQuantity: number     — for "Auto Select First N" button
 *   onConfirm: (selectedSerials: string[]) => void
 */
export const SerialSelectorModal = ({
  isOpen,
  onClose,
  product,
  excludedSerials = [],
  defaultQuantity = 1,
  onConfirm,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [autoN, setAutoN] = useState(defaultQuantity);

  // Reset whenever product changes / modal reopens
  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      setSearch('');
      setAutoN(defaultQuantity || 1);
    }
  }, [isOpen, product?.id, defaultQuantity]);

  const available = useMemo(() => {
    const ex = new Set(excludedSerials || []);
    return (product?.serials || []).filter((s) => s.status === 'In Stock' && !ex.has(s.sn));
  }, [product, excludedSerials]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter((s) => s.sn.toLowerCase().includes(q));
  }, [available, search]);

  const toggle = (sn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((s) => next.add(s.sn));
      return next;
    });
  };

  const deselectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((s) => next.delete(s.sn));
      return next;
    });
  };

  const autoSelectFirstN = () => {
    const n = Math.max(1, parseInt(autoN) || 1);
    const next = new Set();
    available.slice(0, n).forEach((s) => next.add(s.sn));
    setSelected(next);
  };

  const handleConfirm = () => {
    if (selected.size === 0) {
      alert('Select at least one serial.');
      return;
    }
    onConfirm(Array.from(selected));
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? `Select Serials — ${product.name}` : 'Select Serials'}
      size="lg"
    >
      {!product ? (
        <p className="text-sm text-[var(--text-muted)]">No product selected.</p>
      ) : (
        <div className="space-y-3" data-testid="serial-selector-modal">
          {/* Toolbar */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Search Serials</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. RAM001"
                  className="pl-9"
                  data-testid="serial-search"
                />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Auto N</label>
                <Input
                  type="number"
                  min={1}
                  max={available.length || 1}
                  value={autoN}
                  onChange={(e) => setAutoN(e.target.value)}
                  className="w-20"
                  data-testid="serial-auto-n"
                />
              </div>
              <Button type="button" size="sm" variant="outline" onClick={autoSelectFirstN} data-testid="serial-auto-select-btn">
                <Zap size={14} className="mr-1" /> Auto Select First {Math.max(1, parseInt(autoN) || 1)}
              </Button>
            </div>
          </div>

          <div className="flex justify-between items-center text-xs">
            <span className="text-[var(--text-muted)]">
              {filtered.length} of {available.length} available
            </span>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="ghost" onClick={selectAllVisible} data-testid="serial-select-all">
                <CheckSquare size={14} className="mr-1" /> Select All
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={deselectAllVisible} data-testid="serial-deselect-all">
                <Square size={14} className="mr-1" /> Deselect
              </Button>
            </div>
          </div>

          {/* Serial list */}
          <div className="border border-[var(--border)] rounded-lg max-h-[50vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-center text-[var(--text-muted)] py-8">No serials match.</p>
            ) : (
              <ul className="divide-y divide-[var(--border)]">
                {filtered.map((s) => {
                  const checked = selected.has(s.sn);
                  return (
                    <li key={s.sn}>
                      <label
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--surface-hover)] ${checked ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        data-testid={`serial-row-${s.sn}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(s.sn)}
                          className="h-4 w-4 cursor-pointer"
                          data-testid={`serial-check-${s.sn}`}
                        />
                        <span className="font-mono text-sm flex-1">{s.sn}</span>
                        <span className="text-xs text-emerald-600">{s.status}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-[var(--border)]">
            <div className="text-sm">
              <span className="font-semibold text-[var(--brand-cyan)]" data-testid="serial-selected-count">
                {selected.size} selected
              </span>
              {available.length === 0 && (
                <span className="ml-2 text-xs text-amber-600">No serials in stock for this product.</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              <Button type="button" onClick={handleConfirm} disabled={selected.size === 0} data-testid="serial-confirm">
                Add {selected.size > 0 ? `${selected.size} ` : ''}to Invoice
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
